import {Box, Button, Stack, Typography} from '@mui/material';
import {MotorV2Enums, OdontoBookingTaggingEnum} from 'main/analytics/gaTagsEnum';
import {AlertModal, DataInfo} from 'presentation/components';
import {Loading} from 'presentation/components/Loading/Loading';
import {AuthorizationsEnum, OperationStateTypesEnum} from 'presentation/constants';
import {translations} from 'presentation/translations-new';
import React, {Fragment} from 'react';
import {useHealthConfirmBookingModals} from '../../hooks/useHealthConfirmBookingModals';
import {HealthConfirmBookingModalsProps} from './types';

const {
	unavailableSchedule,
	bookingError,
	unexpectedError,
	unauthorizedError,
	openScheduling,
	successBookingDescription,
	successBookingHeaderTitle,
	successBookingAlert,
} = translations['pt-br'].privateFlow.bookingFlow;
const {agreementText} = translations['pt-br'].commons;
const {appointmentProtocol} =
	translations['pt-br'].privateFlow.myAppointments.myAppointmentDetails.appointmentInstructions;

export function HealthConfirmBookingModals({
	isBookingOrReBookingError,
	hasHourOrAvailabilityError,
	isUnexpectedError,
	isUnauthorized,
	isOpenScheduling,
	isDone,
	setBookingState,
	proceedWithBooking,
	setTypeError,
	handleBack,
}: HealthConfirmBookingModalsProps) {
	const {
		handleCallChatRoom,
		handleCloseErrorDialogState,
		handleCloseAndRedirectToInitialFlow,
		handleCloseBackToProfissionalSelection,
		handleCloseAndRedirectToHome,
		handleCloseSuccessDialogState,
		dataTagIdSuccess,
		alertModalpaperStyles,
		protocolNumber,
		callChatRoomIsLoading,
		navigate,
		isHideConfirmationBookingModalEnabled,
	} = useHealthConfirmBookingModals({handleBack, setTypeError, setBookingState});

	return (
		<Fragment>
			<AlertModal
				slotProps={{
					paper: alertModalpaperStyles,
				}}
				description={bookingError.description}
				headerTitle={bookingError.title}
				buttonLabel={bookingError.button}
				onClose={() => handleCloseAndRedirectToInitialFlow(false)}
				open={isBookingOrReBookingError}
				variant="info"
				data-tag-id={MotorV2Enums.V2_ERRO_AGENDAMENTO}
				footerContent={
					<Button
						onClick={() => handleCloseAndRedirectToInitialFlow(true)}
						variant="contained"
						size="large"
						fullWidth
						data-tag-id={MotorV2Enums.V2_CLICK_CTA_ERRO_AGENDAMENTO}>
						{bookingError.button}
					</Button>
				}
			/>

			<AlertModal
				slotProps={{
					paper: alertModalpaperStyles,
				}}
				description={unavailableSchedule.description}
				headerTitle={unavailableSchedule.title}
				buttonLabel={unavailableSchedule.button}
				onClose={handleCloseBackToProfissionalSelection}
				open={hasHourOrAvailabilityError}
				variant="info"
				data-tag-id={MotorV2Enums.V2_ERRO_HORARIO}
				buttonDataTagId={MotorV2Enums.V2_CLICK_CTA_ERRO_HORARIO}
			/>

			<AlertModal
				slotProps={{
					paper: alertModalpaperStyles,
				}}
				description={unexpectedError.description}
				headerTitle={unexpectedError.title}
				buttonLabel={unexpectedError.button}
				onClose={() => handleCloseAndRedirectToHome(false)}
				open={isUnexpectedError}
				variant="info"
				data-tag-id={MotorV2Enums.V2_MODAL_ERRO_GENERICO}
				footerContent={
					<Button
						onClick={() => handleCloseAndRedirectToHome(true)}
						variant="contained"
						size="large"
						fullWidth
						data-tag-id={MotorV2Enums.V2_CLICK_CTA_ERRO_GENERICO}>
						{unexpectedError.button}
					</Button>
				}
			/>

			<AlertModal
				slotProps={{
					paper: alertModalpaperStyles,
				}}
				description={
					<Stack gap={2}>
						<Typography variant="body1" textAlign="center">
							{unauthorizedError.description1}
						</Typography>
						<Typography variant="body1" textAlign="center">
							{unauthorizedError.description2}
						</Typography>
					</Stack>
				}
				headerTitle={unauthorizedError.title}
				buttonLabel={unauthorizedError.button1}
				onClose={handleCloseErrorDialogState}
				open={isUnauthorized}
				variant="info"
				data-tag-id={MotorV2Enums.V2_MODAL_ERRO_GENERICO}
				footerContent={
					<Stack gap={1} width="100%">
						<Button
							onClick={() => navigate(AuthorizationsEnum.AUTHORIZATIONS)}
							variant="contained"
							size="large"
							fullWidth
							data-tag-id={MotorV2Enums.V2_CLICK_CTA_ERRO_GENERICO}>
							{unauthorizedError.button1}
						</Button>
						<Button
							onClick={handleCallChatRoom}
							variant="outlined"
							size="large"
							fullWidth
							data-tag-id={MotorV2Enums.V2_CLICK_CTA_ERRO_GENERICO}
							endIcon={
								callChatRoomIsLoading ? <Loading size={16} color="inherit" /> : null
							}
							disabled={callChatRoomIsLoading}>
							{unauthorizedError.button2}
						</Button>
					</Stack>
				}
			/>

			<AlertModal
				slotProps={{
					paper: alertModalpaperStyles,
				}}
				description={
					<Stack gap={2}>
						<Typography variant="body1" textAlign="center">
							{openScheduling.description2}
						</Typography>
						<Typography variant="body1" textAlign="center">
							{openScheduling.description3}
						</Typography>
					</Stack>
				}
				title={openScheduling.description1}
				headerTitle={openScheduling.title}
				buttonLabel={openScheduling.button1}
				open={isOpenScheduling}
				onClose={() => setBookingState(OperationStateTypesEnum.NONE)}
				variant="info"
				footerContent={
					<Stack gap={1} width="100%">
						<Button
							onClick={proceedWithBooking}
							variant="contained"
							size="large"
							fullWidth>
							{openScheduling.button1}
						</Button>
						<Button
							onClick={() => setBookingState(OperationStateTypesEnum.NONE)}
							variant="outlined"
							size="large"
							fullWidth>
							{openScheduling.button2}
						</Button>
					</Stack>
				}
			/>

			{isHideConfirmationBookingModalEnabled ? null : (
				<AlertModal
					slotProps={{
						paper: alertModalpaperStyles,
					}}
					subDescription={
						<Box alignSelf="center">
							<DataInfo
								primaryText={appointmentProtocol}
								secondaryText={protocolNumber}
								iconName="assignment-filled"
							/>
						</Box>
					}
					headerDataTagId={dataTagIdSuccess}
					buttonDataTagId={
						OdontoBookingTaggingEnum.CLICK_ENTENDI_MODAL_CONFIRMACAO_AGENDAMENTO
					}
					description={successBookingDescription}
					title={successBookingAlert}
					headerTitle={successBookingHeaderTitle}
					open={isDone}
					onClose={handleCloseSuccessDialogState}
					variant="success"
					buttonLabel={agreementText}
				/>
			)}
		</Fragment>
	);
}
